import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronDown, Plus, Search, Trash2 } from 'lucide-react';
import { apiGet, apiPost, apiDel } from '../../lib/api.js';

/**
 * "Saved searches" dropdown for the Leads filter bar: save the current filters
 * under a name, search previous searches, click one to re-apply it.
 * @param {{current: Record<string, *>, onApply: (filters: object) => void}} props
 *   `current` = snapshot of the active filters (empty object disables saving).
 */
export default function SavedSearchesMenu({ current, onApply }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: ['lead-saved-searches'],
    queryFn: () => apiGet('/lead-saved-searches'),
    enabled: open,
  });
  const searches = (data?.searches || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  const canSave = Object.keys(current || {}).length > 0;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const save = useMutation({
    mutationFn: () => apiPost('/lead-saved-searches', { name: name.trim(), filters: current }),
    onSuccess: () => {
      toast.success('Search saved');
      setNaming(false); setName('');
      qc.invalidateQueries({ queryKey: ['lead-saved-searches'] });
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not save search'),
  });
  const remove = useMutation({
    mutationFn: (id) => apiDel(`/lead-saved-searches/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead-saved-searches'] }),
    onError: () => toast.error('Could not delete search'),
  });

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] text-civix-purpleSoft hover:text-white flex items-center gap-1"
        data-testid="saved-searches-toggle"
      >
        <Search className="w-3 h-3" /> Saved searches <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-72 rounded-lg shadow-2xl ring-1 ring-white/10 p-2 z-50 space-y-2" style={{ backgroundColor: '#10102a' }}>
          {naming ? (
            <form
              onSubmit={(e) => { e.preventDefault(); if (name.trim()) save.mutate(); }}
              className="flex gap-1"
            >
              <input
                autoFocus value={name} onChange={(e) => setName(e.target.value)}
                maxLength={80} placeholder="Name this search…" className="input flex-1 text-xs"
              />
              <button type="submit" disabled={!name.trim() || save.isPending} className="btn-primary text-xs px-3">Save</button>
            </form>
          ) : (
            <button
              type="button" disabled={!canSave} onClick={() => setNaming(true)}
              title={canSave ? '' : 'Set at least one filter first'}
              className="w-full flex items-center justify-center gap-1 text-xs font-semibold tracking-wide rounded-md py-2 bg-white/10 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-3 h-3" /> SAVE SEARCH
            </button>
          )}
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search saved searches…" className="input text-xs"
          />
          <div className="max-h-60 overflow-y-auto">
            {searches.length === 0 ? (
              <div className="px-2 py-3 text-xs text-white/40 text-center">No saved searches</div>
            ) : searches.map((s) => (
              <div key={s._id} className="flex items-center group">
                <button
                  type="button"
                  onClick={() => { onApply(s.filters); setOpen(false); }}
                  className="flex-1 text-left px-2 py-2 text-sm text-white hover:bg-white/10 rounded truncate"
                >
                  {s.name}
                </button>
                <button
                  type="button" aria-label={`Delete ${s.name}`}
                  onClick={() => remove.mutate(s._id)}
                  className="p-1 text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
